import { useAuth } from '../contexts/AuthContext'
import { MEMBERSHIP_BILLING_ENABLED } from '../lib/membership'
import { useClubMembership } from './useClubMembership'

/**
 * Where the viewer stands, as the club's chrome and `/the-club` read it.
 *
 * - `member` — a signed-in viewer holding an ACTIVE standing, paid or granted.
 * - `unknown` — a signed-in viewer the club has not been told about: the
 *   standing is still loading, or the read FAILED.
 * - `none` — a visitor, a billing-dark build, or a signed-in viewer whose
 *   standing came back and is not active.
 */
export type Standing = 'member' | 'none' | 'unknown'

/**
 * `member` means `active`, never the tier: a GRANTED standing (a Quartermaster,
 * a demo member) is active and complimentary with no Stripe row behind it, and
 * is in standing exactly like a paid one; a lapsed (`past_due`) or
 * never-settled (`incomplete`) member is NOT, whatever tier they chose, and
 * still needs the selling pages in front of them — for them the material is
 * the next step, not a second bill. `active` is the one bit that says "in
 * standing" for every tier and every way of getting there, and the one the API
 * already publishes.
 *
 * `unknown` is its own answer rather than a flavour of `none` because a
 * reader that treats it as "not a member" sells to members: every cold load of
 * a public page flashed Membership, Terms & tiers and "Apply for membership"
 * at a member for the beat it took to ask, and a read that failed did so for
 * good — while the clubhouse gate, failing OPEN on the same failure, was
 * letting that member in. So the club has not been told, and says so by
 * claiming nothing: the chrome withholds, and `/the-club` keeps its door
 * without a lead-in either way.
 *
 * Two honest `none`s, whatever the standing context holds. Nobody stands
 * anywhere while billing is dark — nothing is asked, nothing is held, and the
 * terms are the only thing the club has to show. And a signed-out visitor is
 * always `none`: sign-out is SPA state (`setUser(null)`, never a reload), and
 * although the standing context follows the session and drops its answer
 * (ClubMembershipContext), this hook does not lean on that — the live session,
 * not the remembered standing, has the first word. (`useAuth` throws without a
 * provider; every club chrome that reads this already calls it, and the
 * showcase wraps one.)
 */
export function useStanding(): Standing {
  const { isAuthenticated } = useAuth()
  const { membership, isLoading, failed } = useClubMembership()
  if (!isAuthenticated || !MEMBERSHIP_BILLING_ENABLED) return 'none'
  if (isLoading || failed) return 'unknown'
  return membership?.active ? 'member' : 'none'
}

/**
 * The chrome's "do not sell" bit — whether to withhold the payment material:
 * the terms, the tiers and every link that leads to them (decision record
 * §12.2, `../../../../../docs/fireside/club-web-decisions.md`).
 *
 * Anything but `none`. A member must never see the join rail — not for the
 * beat it takes to ask, and not because the ask failed — so a signed-in viewer
 * whose standing is unknown is withheld from too; an applicant sees the
 * material a beat later, which costs nothing. A visitor always sees it.
 */
export function useInStanding(): boolean {
  return useStanding() !== 'none'
}
